import React, { useEffect } from 'react';
import dayjs from 'dayjs';
import AppModal from '@/pages/inside/tenant/AppModal';
import { Form, Input, DatePicker, Select, Row, Col } from 'antd';
import { FileOutlined, LinkOutlined, SendOutlined } from '@ant-design/icons';
import { PendingAttachmentTile, PendingLinkWrap } from '@/components/module/projects/sprints_tab/PendingRichTextAttachments';
import { Button } from '@/components/ui/Button';
import RichTextEditor from '@/components/ui/RichTextEditor';
import { useAuthStore } from '@stores/auth.store';
import {
    CreateRoleModalBody,
    CreateRoleModalFooter,
    ModalChromeHeading,
    ModalBlockLabel,
    PanelSectionLabel,
    AvatarStackCircle,
} from '@/pages/inside/tenant/style';
import {
    projectSprintTaskModalFormStyles,
    submissionRichDescriptionFormItemStyle,
    submissionRichDescriptionLabelStyle,
} from '@/pages/inside/tenant/project/projectTabStyles';
import { isLikelyImageAttachmentUrl, resolveAttachmentUrl, getInitials, getAvatarColor } from '@hooks/projects/projectHelpers';
import AddUrlModal from '@/components/module/projects/detail_tab/AddUrlModal';
import TaskAttachmentTile from '@/components/module/projects/task/TaskAttachmentTile';

/**
 * Create sprint, create task, and add-link modals for the Project Sprints tab.
 * Logic lives in {@link useProjectSprintsTabModals}; this file is presentation only.
 */
export default function SprintsTabModals({
    sprintForm,
    taskForm,
    linkModalForm,
    createSprintModalOpen,
    sprintModalMode = 'create',
    closeSprintModal,
    cancelSprintModal,
    resetSprintCreateDraft,
    hasSprintCreateDraft,
    taskModalOpen,
    taskModalMode = 'create',
    closeTaskModal,
    cancelTaskModal,
    resetTaskCreateDraft,
    hasTaskCreateDraft,
    linkModalOpen,
    setLinkModalOpen,
    linkModalCallbackRef,
    mentionUsers,
    mentionMilestones,
    mentionSprints,
    sprintCreateStartDate,
    sprintDateConstraints,
    sprintPendingAttachments,
    sprintExistingAttachments,
    taskPendingAttachments,
    pendingLinkAttachments,
    removePendingLinkAttachment,
    removeExistingSprintAttachment,
    handleEditorLinkRemoved,
    handleSprintFilesSelected,
    handleTaskFilesSelected,
    removeSprintAttachment,
    removeTaskAttachment,
    handleLinkRequest,
    handleLinkModalSubmit,
    handleSprintModalSubmit,
    handleTaskModalSubmit,
    sprintSubmitPending,
    taskSubmitPending,
    truncateFileName,
}) {
    const currentUserId = useAuthStore((s) => s.user?.id ?? null);

    /** Scroll md modals to top when opened so long forms (create task) scroll inside the modal body. */
    useEffect(() => {
        if (!taskModalOpen && !createSprintModalOpen) return;
        const id = requestAnimationFrame(() => {
            document
                .querySelectorAll('.ant-modal-wrap.modal-size-md.ant-modal-wrap-open .ant-modal-body')
                .forEach((el) => {
                    el.scrollTop = 0;
                });
        });
        return () => cancelAnimationFrame(id);
    }, [taskModalOpen, createSprintModalOpen]);

    const sprintTitle = (
        <ModalChromeHeading>{sprintModalMode === 'edit' ? 'Edit sprint' : 'Create sprint'}</ModalChromeHeading>
    );

    const taskTitle = (
        <ModalChromeHeading>{taskModalMode === 'edit' ? 'Edit task' : 'Create task'}</ModalChromeHeading>
    );

    const linkTitle = (
        <ModalChromeHeading>Add link</ModalChromeHeading>
    );

    const sprintFileAttachmentsCombined = [
        ...(sprintModalMode === 'edit'
            ? (sprintExistingAttachments || [])
                .filter((a) => a?.type !== 'link')
                .map((a) => ({ source: 'existing', item: a }))
            : []),
        ...(sprintPendingAttachments || []).map((p) => ({ source: 'pending', item: p })),
    ];

    return (
        <>
            <AppModal
                size="md"
                title={sprintTitle}
                open={createSprintModalOpen}
                onCancel={closeSprintModal}
                footer={
                    <CreateRoleModalFooter $inFooterSlot>
                        {sprintModalMode === 'create' && hasSprintCreateDraft ? (
                            <Button $primary={false} $size="14px" onClick={resetSprintCreateDraft}>
                                Reset
                            </Button>
                        ) : (
                            <Button $primary={false} $size="14px" onClick={cancelSprintModal ?? closeSprintModal}>
                                Cancel
                            </Button>
                        )}
                        <Button
                            $primary
                            $blackOnLight
                            $size="14px"
                            loading={sprintSubmitPending}
                            icon={<SendOutlined />}
                            onClick={handleSprintModalSubmit}
                        >
                            {sprintModalMode === 'edit' ? 'Save changes' : 'Create sprint'}
                        </Button>
                    </CreateRoleModalFooter>
                }
            >
                <CreateRoleModalBody $flush $noMaxHeight>
                    <Form form={sprintForm} layout="vertical" requiredMark={false} styles={projectSprintTaskModalFormStyles}>
                        <Form.Item
                            name="name"
                            label={<span style={submissionRichDescriptionLabelStyle}>Name</span>}
                            rules={[{ required: true, message: 'Enter sprint name' }]}
                        >
                            <Input placeholder="e.g. Sprint 1" size="large" />
                        </Form.Item>
                        <Row gutter={[16, 8]}>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="startDate"
                                    label={<span style={submissionRichDescriptionLabelStyle}>Start date</span>}
                                    rules={[
                                        { required: true, message: 'Select start date' },
                                        () => ({
                                            validator(_, value) {
                                                const min = sprintDateConstraints?.minStartDate;
                                                const max = sprintDateConstraints?.maxEndDate;
                                                if (!value) return Promise.resolve();
                                                const v = dayjs(value).startOf('day');
                                                if (min && v.isBefore(dayjs(min).startOf('day'), 'day')) {
                                                    return Promise.reject(
                                                        new Error(
                                                            `Start date must be on or after ${dayjs(min).format(
                                                                'YYYY-MM-DD',
                                                            )}`,
                                                        ),
                                                    );
                                                }
                                                if (max && v.isAfter(dayjs(max).startOf('day'), 'day')) {
                                                    return Promise.reject(
                                                        new Error(
                                                            `Start date must be on or before the milestone due date (${dayjs(max).format(
                                                                'YYYY-MM-DD',
                                                            )})`,
                                                        ),
                                                    );
                                                }
                                                return Promise.resolve();
                                            },
                                        }),
                                    ]}
                                >
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        size="large"
                                        format="YYYY-MM-DD"
                                        disabledDate={(current) => {
                                            if (!current) return false;
                                            const day = dayjs(current).startOf('day');
                                            const min = sprintDateConstraints?.minStartDate
                                                ? dayjs(sprintDateConstraints.minStartDate).startOf('day')
                                                : null;
                                            const max = sprintDateConstraints?.maxEndDate
                                                ? dayjs(sprintDateConstraints.maxEndDate).startOf('day')
                                                : null;
                                            if (min && day.isBefore(min, 'day')) return true;
                                            if (max && day.isAfter(max, 'day')) return true;
                                            return false;
                                        }}
                                        onChange={(d) => {
                                            const end = sprintForm.getFieldValue('endDate');
                                            if (d && end && end.isBefore(d, 'day')) {
                                                sprintForm.setFieldValue('endDate', null);
                                            }
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="endDate"
                                    label={<span style={submissionRichDescriptionLabelStyle}>End date</span>}
                                    dependencies={['startDate']}
                                    rules={[
                                        { required: true, message: 'Select end date' },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                const start = getFieldValue('startDate');
                                                if (!value || !start) return Promise.resolve();
                                                if (value.isBefore(start, 'day')) {
                                                    return Promise.reject(new Error('End date must be on or after start date'));
                                                }
                                                const max = sprintDateConstraints?.maxEndDate;
                                                if (max && dayjs(value).startOf('day').isAfter(dayjs(max).startOf('day'), 'day')) {
                                                    return Promise.reject(
                                                        new Error(
                                                            `End date must be on or before the milestone due date (${dayjs(max).format(
                                                                'YYYY-MM-DD',
                                                            )})`,
                                                        ),
                                                    );
                                                }
                                                return Promise.resolve();
                                            },
                                        }),
                                    ]}
                                >
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        size="large"
                                        format="YYYY-MM-DD"
                                        disabledDate={(current) => {
                                            if (!current) return false;
                                            const day = dayjs(current).startOf('day');
                                            const start = sprintCreateStartDate ? dayjs(sprintCreateStartDate).startOf('day') : null;
                                            const max = sprintDateConstraints?.maxEndDate
                                                ? dayjs(sprintDateConstraints.maxEndDate).startOf('day')
                                                : null;
                                            if (start && day.isBefore(start, 'day')) return true;
                                            if (max && day.isAfter(max, 'day')) return true;
                                            return false;
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item
                            name="description"
                            style={submissionRichDescriptionFormItemStyle}
                            label={<span style={submissionRichDescriptionLabelStyle}>Description</span>}
                        >
                            <RichTextEditor
                                submissionNeutralLight
                                isAttachment
                                isLink
                                isMention
                                placeholder="Add description — bold, lists, @mentions. Attach files or links."
                                showAttachButton
                                mentionUsers={mentionUsers}
                                mentionMilestones={mentionMilestones}
                                mentionSprints={mentionSprints}
                                onFilesSelected={handleSprintFilesSelected}
                                onLinkRequest={handleLinkRequest}
                                onLinkRemoved={handleEditorLinkRemoved}
                                attachmentIndexOffset={sprintPendingAttachments.length}
                                onAttachmentRemoved={(index) => {
                                    const p = sprintPendingAttachments[index];
                                    if (p) removeSprintAttachment(p.id);
                                }}
                            />
                        </Form.Item>
                        {sprintModalMode === 'edit' && sprintExistingAttachments?.some((a) => a?.type === 'link') ? (
                            <PendingLinkWrap
                                links={sprintExistingAttachments
                                    .filter((a) => a?.type === 'link')
                                    .map((a) => ({
                                        id: a.id,
                                        url: a.url,
                                        title: a.title || a.url,
                                    }))}
                                onRemoveLink={removeExistingSprintAttachment}
                            />
                        ) : null}
                        {pendingLinkAttachments.length > 0 && createSprintModalOpen && (
                            <PendingLinkWrap links={pendingLinkAttachments} onRemoveLink={removePendingLinkAttachment} />
                        )}
                        {sprintFileAttachmentsCombined.length > 0 && (
                            <div
                                style={{
                                    marginTop: 8,
                                    marginBottom: 16,
                                    padding: '8px 0',
                                    borderTop: '1px solid var(--ant-color-border, #f0f0f0)',
                                }}
                            >
                                <PanelSectionLabel
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        marginBottom: 8,
                                        fontSize: 11,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.04em',
                                        opacity: 0.75,
                                    }}
                                >
                                    <FileOutlined />
                                    Attachments
                                </PanelSectionLabel>
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                                        gap: 10,
                                    }}
                                >
                                    {sprintFileAttachmentsCombined.map((entry) => {
                                        if (entry.source === 'pending') {
                                            const p = entry.item;
                                            return (
                                                <TaskAttachmentTile
                                                    key={`pending-${p.id}`}
                                                    href={p.preview}
                                                    title={p.name}
                                                    isImage={(p.type || '').startsWith('image/')}
                                                    ext={p.name.split('.').pop()}
                                                    onPreviewImage={p.preview ? (href) => window.open(href, '_blank') : undefined}
                                                    onOpenFile={p.preview ? (href) => window.open(href, '_blank') : undefined}
                                                    onRemove={() => removeSprintAttachment(p.id)}
                                                />
                                            );
                                        }
                                        const a = entry.item;
                                            const fallbackName = typeof a?.url === 'string' ? a.url.split('/').pop() : '';
                                            const resolvedUrl = resolveAttachmentUrl(a?.url || '');
                                            const isImage = isLikelyImageAttachmentUrl(a?.url || a?.title || '');
                                            return (
                                                <TaskAttachmentTile
                                                    key={`existing-${a.id}`}
                                                    href={resolvedUrl}
                                                    title={a?.title || fallbackName || 'attachment'}
                                                    isImage={isImage}
                                                    ext={a?.title ? a.title.split('.').pop() : fallbackName.split('.').pop()}
                                                    onPreviewImage={resolvedUrl ? (href) => window.open(href, '_blank') : undefined}
                                                    onOpenFile={resolvedUrl ? (href) => window.open(href, '_blank') : undefined}
                                                    onRemove={() => removeExistingSprintAttachment(a.id)}
                                                />
                                            );
                                        })}
                                </div>
                            </div>
                        )}
                    </Form>
                </CreateRoleModalBody>
            </AppModal>

            <AppModal
                size="md"
                title={taskTitle}
                open={taskModalOpen}
                onCancel={closeTaskModal}
                footer={
                    <CreateRoleModalFooter $inFooterSlot>
                        {taskModalMode === 'create' && hasTaskCreateDraft ? (
                            <Button $primary={false} $size="14px" onClick={resetTaskCreateDraft}>
                                Reset
                            </Button>
                        ) : (
                            <Button $primary={false} $size="14px" onClick={cancelTaskModal ?? closeTaskModal}>
                                Cancel
                            </Button>
                        )}
                        <Button
                            $primary
                            $blackOnLight
                            $size="14px"
                            loading={taskSubmitPending}
                            icon={<SendOutlined />}
                            onClick={handleTaskModalSubmit}
                        >
                            {taskModalMode === 'edit' ? 'Save changes' : 'Create task'}
                        </Button>
                    </CreateRoleModalFooter>
                }
            >
                <CreateRoleModalBody $flush $noMaxHeight>
                    <Form form={taskForm} layout="vertical" requiredMark={false} styles={projectSprintTaskModalFormStyles}>
                        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.title !== curr.title}>
                            {() => {
                                const errors = taskForm.getFieldError('title');
                                const errorMsg = errors?.[0];
                                const titleValue = taskForm.getFieldValue('title') ?? '';
                                const charCount = titleValue.length;

                                return (
                                    <>
                                        <Row gutter={[16, 12]}>
                                            <Col xs={24}>
                                                <Form.Item
                                                    name="title"
                                                    label={<span style={submissionRichDescriptionLabelStyle}>Title</span>}
                                                    rules={[{ required: true, message: 'Enter task title' }]}
                                                    validateStatus={errorMsg ? 'error' : ''}
                                                    help={
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',  }}>
                                                            <div>
                                                                {errorMsg && (
                                                                    <span style={{ color: 'var(--ant-color-error, #ff4d4f)', fontSize: 11, fontWeight: 500 }}>
                                                                        {errorMsg}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <span style={{ color: 'var(--ant-color-text-description, #8c8c8c)', fontSize: 11 }}>
                                                                {charCount} / 100
                                                            </span>
                                                        </div>
                                                    }
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <Input placeholder="e.g. Implement login page" size="large" maxLength={100} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </>
                                );
                            }}
                        </Form.Item>
                        <Row gutter={[16, 12]} style={{ marginBottom: 8 }}>
                            {taskModalMode === 'create' ? (
                                <>
                                    <Col xs={24} md={10}>
                                        <Form.Item
                                            name="assigneeId"
                                            label={<span style={submissionRichDescriptionLabelStyle}>Assignee</span>}
                                        >
                                            <Select
                                                showSearch
                                                placeholder="Assign to..."
                                                size="large"
                                                allowClear
                                                popupMatchSelectWidth={false}
                                                filterOption={(input, option) =>
                                                    (option?.searchLabel ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                                optionRender={(option) => {
                                                    return (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' }}>
                                                            <AvatarStackCircle
                                                                $bg={option.data.avatarColor}
                                                                $color="#fff"
                                                                style={{
                                                                    width: 28,
                                                                    height: 28,
                                                                    borderRadius: 999,
                                                                    fontSize: 11,
                                                                    fontWeight: 700,
                                                                    lineHeight: 1,
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    flexShrink: 0,
                                                                }}
                                                            >
                                                                {option.data.initials}
                                                            </AvatarStackCircle>
                                                            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, minWidth: 0 }}>
                                                                <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--ant-color-text)', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                                                    {option.data.userName}
                                                                </span>
                                                                {option.data.userSub && (
                                                                    <span style={{ fontSize: 11, color: 'var(--ant-color-text-secondary)', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                                                        {option.data.userSub}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                }}
                                                options={(mentionUsers || [])
                                                    .filter(u => u && String(u.id) !== String(currentUserId))
                                                    .map((u) => {
                                                        const userName = (u.name ?? u.fullName ?? u.displayName ?? u.email ?? u.username ?? String(u.id ?? '')).trim();
                                                        const initials = getInitials(userName);
                                                        const avatarColor = getAvatarColor(String(u.id ?? userName));
                                                        const userSub = u.email ?? '';
                                                        return {
                                                            value: String(u.id),
                                                            searchLabel: userName,
                                                            userName,
                                                            initials,
                                                            avatarColor,
                                                            userSub,
                                                            label: (
                                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                                                    <AvatarStackCircle
                                                                        $bg={avatarColor}
                                                                        $color="#fff"
                                                                        style={{
                                                                            width: 22,
                                                                            height: 22,
                                                                            borderRadius: 999,
                                                                            fontSize: 10,
                                                                            fontWeight: 700,
                                                                            lineHeight: 1,
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            flexShrink: 0,
                                                                        }}
                                                                    >
                                                                        {initials}
                                                                    </AvatarStackCircle>
                                                                    <span style={{ fontSize: 13, fontWeight: 500 }}>{userName}</span>
                                                                </div>
                                                            )
                                                        };
                                                    })}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={12} md={7}>
                                        <Form.Item
                                            name="priority"
                                            label={<span style={submissionRichDescriptionLabelStyle}>Priority</span>}
                                            initialValue="medium"
                                        >
                                            <Select
                                                size="large"
                                                options={[
                                                    { value: 'high', label: 'High' },
                                                    { value: 'medium', label: 'Medium' },
                                                    { value: 'low', label: 'Low' },
                                                ]}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={12} md={7}>
                                        <Form.Item
                                            name="type"
                                            label={<span style={submissionRichDescriptionLabelStyle}>Task Type</span>}
                                            initialValue="feature"
                                            rules={[{ required: true, message: 'Select task type' }]}
                                        >
                                            <Select
                                                size="large"
                                                options={[
                                                    { value: 'feature', label: 'Feature' },
                                                    { value: 'improvement', label: 'Improvement' },
                                                ]}
                                            />
                                        </Form.Item>
                                    </Col>
                                </>
                            ) : (
                                <>
                                    <Col xs={12} md={12}>
                                        <Form.Item
                                            name="priority"
                                            label={<span style={submissionRichDescriptionLabelStyle}>Priority</span>}
                                            initialValue="medium"
                                        >
                                            <Select
                                                size="large"
                                                options={[
                                                    { value: 'high', label: 'High' },
                                                    { value: 'medium', label: 'Medium' },
                                                    { value: 'low', label: 'Low' },
                                                ]}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={12} md={12}>
                                        <Form.Item
                                            name="type"
                                            label={<span style={submissionRichDescriptionLabelStyle}>Task Type</span>}
                                            initialValue="feature"
                                            rules={[{ required: true, message: 'Select task type' }]}
                                        >
                                            <Select
                                                size="large"
                                                options={[
                                                    { value: 'feature', label: 'Feature' },
                                                    { value: 'improvement', label: 'Improvement' },
                                                ]}
                                            />
                                        </Form.Item>
                                    </Col>
                                </>
                            )}
                        </Row>
                        <Row gutter={[16, 12]}>
                            <Col xs={24}>
                                <Form.Item
                                    name="description"
                                    style={submissionRichDescriptionFormItemStyle}
                                    label={<span style={submissionRichDescriptionLabelStyle}>Description</span>}
                                >
                                    <RichTextEditor
                                        submissionNeutralLight
                                        isAttachment
                                        isLink
                                        isMention
                                        placeholder="Add description — bold, lists, @mentions. Attach files or links."
                                        showAttachButton
                                        mentionUsers={mentionUsers}
                                        mentionMilestones={mentionMilestones}
                                        mentionSprints={mentionSprints}
                                        onFilesSelected={handleTaskFilesSelected}
                                        onLinkRequest={handleLinkRequest}
                                        onLinkRemoved={handleEditorLinkRemoved}
                                        attachmentIndexOffset={taskPendingAttachments.length}
                                        onAttachmentRemoved={(index) => {
                                            const p = taskPendingAttachments[index];
                                            if (p) removeTaskAttachment(p.id);
                                        }}
                                    />
                                </Form.Item>
                                {taskPendingAttachments.length > 0 && (
                                    <div
                                        style={{
                                            marginTop: 8,
                                            marginBottom: 16,
                                            padding: '8px 0',
                                            borderTop: '1px solid var(--ant-color-border, #f0f0f0)',
                                        }}
                                    >
                                        <PanelSectionLabel
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                marginBottom: 8,
                                                fontSize: 11,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.04em',
                                                opacity: 0.75,
                                            }}
                                        >
                                            <FileOutlined />
                                            Attachments
                                        </PanelSectionLabel>
                                        <div
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                                                gap: 10,
                                            }}
                                        >
                                            {taskPendingAttachments.map((p) => {
                                                return (
                                                    <TaskAttachmentTile
                                                        key={p.id}
                                                        href={p.preview}
                                                        title={p.name}
                                                        isImage={(p.type || '').startsWith('image/')}
                                                        ext={p.name.split('.').pop()}
                                                        onPreviewImage={p.preview ? (href) => window.open(href, '_blank') : undefined}
                                                        onOpenFile={p.preview ? (href) => window.open(href, '_blank') : undefined}
                                                        onRemove={() => removeTaskAttachment(p.id)}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                                {pendingLinkAttachments.length > 0 && taskModalOpen && (
                                    <PendingLinkWrap links={pendingLinkAttachments} onRemoveLink={removePendingLinkAttachment} />
                                )}
                            </Col>
                        </Row>
                    </Form>
                </CreateRoleModalBody>
            </AppModal>

            <AddUrlModal
                open={linkModalOpen}
                onCancel={() => {
                    setLinkModalOpen(false);
                    linkModalCallbackRef.current = null;
                }}
                form={linkModalForm}
                onSubmit={handleLinkModalSubmit}
                title={linkTitle}
            />
        </>
    );
}
